import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { MapPinIcon } from 'lucide-react';
import { HeadingText } from './HeadingText';
import { MediaView } from './MediaView';
import { Section } from './Section';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useNow } from '../../hooks/useNow';
import { useCompactMotion } from '../../hooks/useParallax';
import { closedDaysLabel, salon } from '../../data/content';
import { pageActions } from '../../data/pageActions';
import { useSite } from '../../contexts/SiteContext';
import { visitStatus } from '../../core/rules/visitStatus';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { btnPrimaryLg } from '../../ui/styles';
import type { Heading } from '../../core/types/pages';
import type { Media } from '../../core/types/media';
type Props = {
  titleId: string;
  eyebrow?: string;
  heading: Heading;
  body?: string;
  /** Label van de ene knop (plan een afspraak). Leeg = geen knop. */
  cta?: string;
  media: Media;
  /** Rustige links onder de knop (route, salonpagina, WhatsApp). */
  links?: ReactNode;
  /**
   * `arch`: het beeld in een boog die bij het scrollen opengaat (het moment van de homepage).
   * `still`: stil beeld, voor pagina's die hun ene moment elders hebben (Over).
   */
  motion?: 'arch' | 'still';
};

/**
 * "Kom langs": één blok voor elke pagina die naar de salon verwijst (Home, Over), op blush.
 * Status (wanneer je weer terecht kunt), kop, adres als tekst, één knop en rustige links.
 * Adres en dagen uit Instellingen › Salon & contact en › Salondagen; de woorden per pagina uit Paginateksten.
 * De lange versie (kaart, route, tijden) staat op Contact.
 */
export function VisitBlock({
  titleId,
  eyebrow,
  heading,
  body,
  cta,
  media,
  links,
  motion: kind = 'still'
}: Props) {
  const {
    openBooking
  } = useOverlayActions();
  const {
    openingHours,
    salonExceptions
  } = useSite();
  const now = useNow();
  const closed = closedDaysLabel(openingHours);
  const status = visitStatus(openingHours, salonExceptions, now);
  return <Section tone="blush" labelledBy={titleId} className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16" data-id="element-8857" data-mp="1p2u7kzydanb0">
        {kind === 'arch' ? <ArchImage media={media} data-id="element-8858" data-mp="1y0wd9st1q44m" /> : <MediaView media={media} sizes="(min-width: 1024px) 40vw, 100vw" className="aspect-[4/3] w-full lg:col-span-5 lg:aspect-[4/5]" data-id="element-8859" data-mp="1xzczc8uv4560" />}

        <div className="lg:col-span-7" data-id="element-8860" data-mp="15djttri6mcfj">
          <p className="flex items-center gap-2.5 text-[14px] text-ink" data-id="element-8861" data-mp="15afima6augfd">
            <span aria-hidden="true" className={`h-2 w-2 rounded-full ${status.open ? 'bg-sage' : 'bg-ink/30'}`} data-id="element-8862" data-mp="1k26gkea526gs" />
            {status.label}
          </p>
          {eyebrow && <p className="mt-6 text-[14px] text-ink" data-id="element-8863" data-mp="rkkcrl2r4a5v">{fill(eyebrow)}</p>}
          <h2 id={titleId} className={`${eyebrow ? 'mt-3' : 'mt-5'} ${sectionHeading} text-ink`} data-id="element-8864" data-mp="rir9mfrck82m">
            <HeadingText heading={heading} accentBlock data-id="element-8865" data-mp="mk6eguqycel1" />
          </h2>
          {body && <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-muted" data-id="element-8866" data-mp="2y81h0k3nfina">{fill(body)}</p>}

          <address className="mt-8 flex max-w-lg gap-3 border-t border-ink/15 pt-6 text-[15px] not-italic leading-relaxed text-ink" data-id="element-8867" data-mp="331dwo4qcof3w">
            <MapPinIcon className="mt-1 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8868" data-mp="2ta02mch647i5" />
            <span data-id="element-8869" data-mp="3gzub6hugf9wl">
              {salon.street}, {salon.postalCode} {salon.locality}
              <span className="block text-muted" data-id="element-8870" data-mp="3gy17nbf515st">
                {pageActions.byAppointment}
                {closed ? ` · ${closed.toLowerCase()}` : ''} ·{' '}
                <Link to="/contact" className="underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink" data-id="element-8871" data-mp="37yntw6jzdv2a">
                  tijden, parkeren en aanbellen
                </Link>
              </span>
            </span>
          </address>

          {cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={`mt-8 ${btnPrimaryLg} w-full sm:w-auto`} data-id="element-8872" data-mp="21k4yh9onid9f">
              {fill(cta)}
            </button>}
          {links && <div className="mt-4 flex flex-wrap items-center gap-x-6" data-id="element-8873" data-mp="2c18l2aj44kvw">{links}</div>}
        </div>
    </Section>;
}

/** De boog die opengaat als een deur: smal → volle boog, terwijl het beeld uitzoomt. Minder beweging: meteen open. */
function ArchImage({
  media
}: {
  media: Media;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const compact = useCompactMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const scale = useTransform(scrollYProgress, [0, 0.6], [1.15, 1]);
  const side = compact ? 14 : 28;
  const clipPath = useTransform(scrollYProgress, [0.05, 0.5], [`inset(0% ${side}% 0% ${side}% round 9999px 9999px 0 0)`, 'inset(0% 0% 0% 0% round 9999px 9999px 0 0)']);
  return <div ref={ref} className="mx-auto w-full max-w-xs sm:max-w-sm lg:col-span-5 lg:max-w-none" data-id="element-8874" data-mp="1ac543rnv3so1">
      {/* clip-path i.p.v. breedte: het beeld vervormt niet en de pagina verspringt niet. */}
      <motion.div style={reduce ? undefined : {
      clipPath
    }} className="relative aspect-[4/5] w-full overflow-hidden rounded-t-full bg-sand" data-id="element-8875" data-mp="3qyuxn68ojrlh">
        <motion.div style={reduce ? undefined : {
        scale
      }} className="absolute inset-0" data-id="element-8876" data-mp="3uz55ypjy2u3i">
          <MediaView media={media} sizes="(min-width: 1024px) 38vw, 100vw" className="h-full w-full" data-id="element-8877" data-mp="3mu1m4dvy2vh" />
        </motion.div>
      </motion.div>
    </div>;
}